
import * as Yup from "yup";
import { FormikErrors } from "formik";
import { Plan } from "../dataTypes";

export const planDetailsValidation = Yup.object().shape({
  title: Yup.string()
    .min(3, "Plan title must be at least 3 characters")
    .max(100, "Plan title cannot exceed 100 characters")
    .required("Plan title is required"),
  description: Yup.string()
    .max(500, "Description cannot exceed 500 characters")
    .required("Plan description is required"),
});

export const pricingValidation = Yup.object().shape({
  prices: Yup.array()
    .of(
      Yup.object().shape({
        billingInterval: Yup.string().oneOf(["month", "year"]).required("Interval is required"),
        active: Yup.boolean(),
        amount: Yup.number()
          .typeError("Price amount must be a number")
          .min(0, "Price amount cannot be negative")
          .required("Price amount is required"),
        currency: Yup.string()
          .length(3, "Currency must be a valid 3-letter ISO code (e.g., USD)")
          .required("Currency is required"),
        symbol: Yup.string().max(5, "Symbol too long").required("Currency symbol is required"),
      })
    )
    .min(1, "At least one price option is required"),
  userLimit: Yup.number()
    .typeError("User limit must be a number")
    .min(0, "User limit cannot be negative"),
  contactLimit: Yup.number()
    .typeError("Contact limit must be a number")
    .min(0, "Contact limit cannot be negative"),
});

export const featuresValidation = Yup.object().shape({
  saasProducts: Yup.array()
    .of(Yup.string())
    .min(1, "At least one SaaS product must be selected"),
});

export const trialAndCreditsValidation = Yup.object().shape({
  trialPeriod: Yup.object().shape({
    active: Yup.boolean(),
    days: Yup.number()
      .typeError("Trial days must be a number")
      .min(0, "Trial period days cannot be negative")
      .max(365, "Trial period cannot exceed one year")
      .when("active", {
        is: true,
        then: (schema) => schema
          .min(1, "Trial period days must be > 0 when trial is active")
          .required("Trial period days are required when active"),
      }),
  }),
  credits: Yup.object().shape({
    active: Yup.boolean(),
    creditType: Yup.string().oneOf(["recurring", "onetime"]).nullable(),
    amount: Yup.number()
      .typeError("Credit amount must be a number")
      .min(0, "Credit amount cannot be negative")
      .when("active", {
        is: true,
        then: (schema) => schema
          .min(1, "Credit amount must be > 0 when credits are active")
          .required("Credit amount is required when active"),
      }),
  }).test("credit-type-required", "Credit type is required when credits are active", function (value) {
    if (value.active && !value.creditType) {
      return this.createError({
        path: "credits.creditType",
        message: "Credit type is required when credits are active",
      });
    }
    return true;
  }),
});

export const rebillingValidation = Yup.object().shape({
  rebilling: Yup.array().of(
    Yup.object().shape({
      interval: Yup.string().oneOf(["month", "year"]).required("Rebilling interval is required"),
      amount: Yup.number()
        .typeError("Rebilling amount must be a number")
        .min(0, "Rebilling amount cannot be negative")
        .required("Rebilling amount is required"),
      active: Yup.boolean(),
    })
  ),
});

export const combinedValidationSchema = Yup.object().shape({
  ...planDetailsValidation.fields,
  ...pricingValidation.fields,
  ...featuresValidation.fields,
  ...trialAndCreditsValidation.fields,
  ...rebillingValidation.fields,
});

export const getTabErrors = (errors: FormikErrors<Plan>): { [key: string]: boolean } => {
  const tabFieldMapping: { [key: string]: (keyof Plan | string)[] } = {
    "plan-details": ['title', 'description'],
    "pricing": ['prices', 'userLimit', 'contactLimit'],
    "features": ['saasProducts'],
    "trial": ['trialPeriod.days', 'credits.amount', 'credits.creditType'],
    "rebilling": ['rebilling'],
  };

  const hasNestedError = (errors: any, fields: string[]): boolean => {
    for (const field of fields) {
      if (field.includes('.')) {
        const [parent, child] = field.split('.');
        if (errors[parent] && errors[parent][child]) return true;
      } else if (errors[field] || (Array.isArray(errors[field]) && errors[field].length > 0)) {
        if (Array.isArray(errors[field])) {
          if (errors[field].some((item: any) => item && Object.keys(item).length > 0)) return true;
        } else {
          return true;
        }
      }
    }
    return false;
  };

  const tabErrorStatus: { [key: string]: boolean } = {};
  const tabs = ["plan-details", "pricing", "features", "trial", "rebilling"];
  for (const tab of tabs) {
    tabErrorStatus[tab] = hasNestedError(errors, tabFieldMapping[tab]);
  }
  return tabErrorStatus;
};